import React, { useCallback } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { RegisterFormSchema } from "../forms/RegisterFormSchema";
import { useLoginAndRegisterStore } from "../stores/LoginAndRegisterStore";
import { useMutation } from "@tanstack/react-query";
import { registerUser } from "../backend/api/auth";
import { RegisterRequest, RegisterResponse } from "../backend/types/api-types";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { useUserStore } from "../stores/UserStore";
import { trackRegistration } from "../utils/analytics";
import GearInput from "../ui/gearinput";
import GearButton from "../ui/gearbutton";
export type RegisterFormType = z.infer<typeof RegisterFormSchema>;

interface RegisterFormProps {}

const RegisterForm: React.FC<RegisterFormProps> = () => {
  const { t } = useTranslation();
  const { close, openLogin } = useLoginAndRegisterStore();
  const { setUser } = useUserStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<RegisterFormType>({
    resolver: zodResolver(RegisterFormSchema),
    mode: "onTouched",
  });
  console.log({ errors, isValid });

  const { mutate: registerMutation, isPending } = useMutation({
    mutationFn: (value: RegisterRequest) => registerUser(value, i18n.language),
    onSuccess: (data: RegisterResponse) => {
      toast.success(data.message);
      setUser({ ...data.user, access_token: data.access_token });

      // Track registration in Google Analytics
      trackRegistration("email", data.user.id);

      close();
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response.data.message);
    },
  });

  const onSubmit: SubmitHandler<RegisterFormType> = useCallback(
    async (value: RegisterFormType) => {
      registerMutation({
        email: value.email,
        firstName: value.firstName,
        lastName: value.lastName,
        password: value.password,
      });
    },
    []
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <GearInput
          label={t("forms.firstName", "Nome")}
          type="text"
          icon={<img src="/icons/user.svg" alt="People" className="w-4 h-4" />}
          {...register("firstName")}
          placeholder={t("forms.firstName", "Nome")}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-primary focus:outline-none transition-all"
          error={errors.firstName ? t(errors.firstName.message!) : undefined}
          required
        />


        <GearInput
          label={t("forms.lastName", "Cognome")}
          type="text"
          icon={<img src="/icons/user.svg" alt="People" className="w-4 h-4" />}
          {...register("lastName")}
          placeholder={t("forms.lastName", "Cognome")}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-primary focus:outline-none transition-all"
          error={errors.lastName ? t(errors.lastName.message!) : undefined}
          required
        />
        <GearInput
          label={t("forms.email", "Email")}
          type="email"
          icon={<img src="/icons/mail.svg" alt="Mail" className="w-4 h-4" />}
          {...register("email")}
          placeholder={t("forms.email", "Email")}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-primary focus:outline-none transition-all"
          error={errors.email ? t(errors.email.message!) : undefined}
          required
        />

        <GearInput
          label={t("forms.password", "Password")}
          type="password"
          icon={<img src="/icons/lock.svg" alt="Lock" className="w-4 h-4" />}
          {...register("password")}
          placeholder={t("forms.password", "Password")}
          error={errors.password ? t(errors.password.message!) : undefined}
          required
        />

        <GearInput
          label={t("forms.repeatPassword", "Ripeti password")}
          type="password"
          icon={<img src="/icons/lock.svg" alt="Lock" className="w-4 h-4" />}
          {...register("repeatPassword")}
          placeholder={t("forms.repeatPassword", "Ripeti password")}
          error={errors.repeatPassword ? t(errors.repeatPassword.message!) : undefined}
          required
        />

        <GearButton
          variant="secondary"
          type="submit"
          size="medium"
          className="w-full rounded-full"
          disabled={!isValid || isPending}
        >
          {t("forms.register", "Registrati")}
        </GearButton>
        <p className="text-sm text-gray-600 text-center pt-2">
          {t("forms.alreadyHaveAccount", "Hai già un account?")}{" "}
          <span
            className="text-primary hover:text-primary-600 cursor-pointer font-medium"
            onClick={() => openLogin()}
          >
            {t("forms.login", "Accedi")}
          </span>
        </p>
      </form>
    </>
  );
};

export default RegisterForm;
